import { Button } from "@/components/ui/button";
import { CalendarDays, Linkedin } from "lucide-react";
import { Link } from "react-router-dom";
import type { TeamMember } from "@/lib/teamData";

interface ExecutiveProfileCardProps {
  member: TeamMember;
  size?: "large" | "medium";
  priority?: boolean;
}

const ExecutiveProfileCard = ({ member, priority = false }: ExecutiveProfileCardProps) => {
  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-2xl border border-[hsl(var(--line))] bg-gradient-to-b from-[#05111f] to-[#02060d]">
      <Link
        to={`/team/${member.slug}`}
        aria-label={`View ${member.name} profile`}
        className="block overflow-hidden bg-black/40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
      >
        <img
          src={member.image}
          alt={`${member.name}, ${member.title} at Neil AI Global Solutions`}
          width={800}
          height={1000}
          className="aspect-[4/5] w-full object-contain object-center transition-transform duration-500 group-hover:scale-[1.02]"
          loading={priority ? "eager" : "lazy"}
          {...({ fetchpriority: priority ? "high" : "auto" } as any)}
        />
      </Link>

      <div className="flex flex-1 flex-col gap-2 p-4 sm:p-5">
        <h3 className="font-orbitron text-lg font-bold leading-snug text-foreground">{member.name}</h3>
        <p className="text-sm font-semibold text-primary">{member.title}</p>
        <p className="text-sm leading-relaxed text-muted-foreground line-clamp-4">{member.shortBio}</p>

        <div className="mt-auto flex flex-wrap items-center gap-2 pt-3">
          <Button asChild className="min-h-11 rounded-full bg-primary px-4 text-xs text-primary-foreground hover:bg-primary/90">
            <a href={member.contactUrl} target="_blank" rel="noopener noreferrer" aria-label={`Book a consultation with ${member.name}`}>
              <CalendarDays className="mr-1.5 h-3.5 w-3.5" />
              Book a Consultation
            </a>
          </Button>

          <Button
            asChild
            variant="outline"
            className="min-h-11 w-11 rounded-full border-accent/50 bg-background/40 p-0 text-accent hover:bg-accent/10 hover:text-accent"
          >
            <a href={member.linkedin} target="_blank" rel="noopener noreferrer" aria-label={`${member.name} on LinkedIn`}>
              <Linkedin className="h-4 w-4" />
            </a>
          </Button>

          <Link
            to={`/team/${member.slug}`}
            className="ml-auto min-h-11 inline-flex items-center text-xs font-semibold uppercase tracking-[0.12em] text-foreground/90 transition-colors hover:text-primary"
          >
            Full profile
          </Link>
        </div>
      </div>
    </article>
  );
};

export default ExecutiveProfileCard;
